import { gql, useQuery } from '@apollo/client'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistCollectionsQuery,
  ArtistCollectionsQueryVariables,
  CollectionBoxFragmentDoc,
  CurrentUserFragmentDoc,
  FetchNewArtistCollectionsQuery,
  FetchNewArtistCollectionsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistCollectionsQuery,
    FetchNewArtistCollectionsQueryVariables
  >({
    query: FETCH_NEW_COLLECTION,
    variables: {
      artistId,
      pageInput: { limit: 20 },
    },
    fields: [
      { fieldName: 'userCollections', fragment: CollectionBoxFragmentDoc },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, fetchMore, loading } = useQuery<
    ArtistCollectionsQuery,
    ArtistCollectionsQueryVariables
  >(QUERY, {
    variables: {
      userCollectionsArtistId: artistId,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted({ userCollections, artist }) {
      isFirstLoadRef.current = false
      if (!userCollections?.edges?.length) {
        analytics?.track('impression_noData', {
          applicationKey: config.applicationKey,
          artistId: artistId,
          name: 'artistCollections',
        })
      }
      if (artist) {
        currentArtistVar(artist)
      }
    },
  })
  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      artistName: data?.artist?.displayName || '',
      user: data?.user || null,
      artistId,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      hasNextPage: !!data?.userCollections?.pageInfo?.hasNextPage,
      userCollections: data?.userCollections ?? null,
      liveStreamId: data?.artist?.currentLive?.id,
    },
    actions: {
      async onFetchMoreArtistCollection() {
        await fetchMore({
          variables: {
            userCollectionsArtistId: artistId,
            userCollectionsPageInput: {
              cursor: data?.userCollections?.pageInfo.endCursor || null,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistCollections(
    $userCollectionsPageInput: PageInputDate
    $userCollectionsArtistId: ID
  ) {
    artist(id: $userCollectionsArtistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    user {
      ...CurrentUser
    }
    userCollections(
      pageInput: $userCollectionsPageInput
      artistId: $userCollectionsArtistId
    ) {
      edges {
        node {
          ...CollectionBox
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${CollectionBoxFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_COLLECTION = gql`
  query FetchNewArtistCollections($pageInput: PageInputDate, $artistId: ID) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    userCollections(pageInput: $pageInput, artistId: $artistId) {
      edges {
        node {
          ...CollectionBox
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${CollectionBoxFragmentDoc}
`
